import { Schema } from 'effect';

import type { ListingState } from '../capability/productListing/listingSelection';
import type { ListingScope } from '../capability/productListing/listingScope';
import type { ListingUrlSyntax } from './url/ListingUrlSyntax';
import {
  equalsUrl,
  ListingUrl as ListingUrlSchema,
  type ListingUrl,
  type QueryPair,
} from './url/listingUrl';
import { rangeCodesOf, type ListingVocabulary } from './vocabulary/ListingVocabulary';
import { toSelection, toTokens } from './vocabulary/translate';

export type ListingBase = {
  readonly url: ListingUrl;
  readonly preserved: ReadonlyArray<QueryPair>;
};

export const ListingBaseSchema = Schema.Struct({
  url: ListingUrlSchema,
  preserved: Schema.Array(Schema.Tuple([Schema.String, Schema.String])),
});

export type DecodedListing = {
  readonly state: ListingState;
  readonly base: ListingBase;
  readonly canonicalUrl: ListingUrl;
  readonly isCanonical: boolean;
  readonly dropped: ReadonlyArray<string>;
  readonly unknown: ReadonlyArray<string>;
};

/** Decodes a syntax-specific URL through the tenant vocabulary. */
export const decodeListingUrl = (
  url: ListingUrl,
  scope: ListingScope,
  syntax: ListingUrlSyntax,
  vocabulary: ListingVocabulary
): DecodedListing => {
  const parsed = syntax.read(url, { rangeCodes: rangeCodesOf(vocabulary) });
  const translated = toSelection(parsed.tokens, vocabulary);
  const state = { scope, selection: translated.selection, page: translated.page };
  const base = { url: parsed.base, preserved: parsed.preserved };
  const canonicalUrl = encodeListingUrl(base, state, syntax, vocabulary);

  return {
    state,
    base,
    canonicalUrl,
    isCanonical: equalsUrl(url, canonicalUrl),
    dropped: parsed.dropped,
    unknown: translated.unknown,
  };
};

/** Encodes a listing state through the tenant vocabulary and URL syntax. */
export const encodeListingUrl = (
  base: ListingBase,
  state: ListingState,
  syntax: ListingUrlSyntax,
  vocabulary: ListingVocabulary
): ListingUrl => {
  const translated = toTokens(state.selection, state.page, vocabulary);
  return syntax.write(base.url, translated.tokens, base.preserved);
};
